import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import styled, { createGlobalStyle } from 'styled-components';

type Placement = 'page' | 'inline';

interface DraftBannerProps {
  itemKey: string;
  draft: boolean;
  placement?: Placement;
}

const PageSpacing = createGlobalStyle`
  :root { --draft-banner-height: 36px; }
  #root { padding-top: var(--draft-banner-height); }

  @media (max-width: 1023px) {
    :root { --draft-banner-height: 44px; }
  }
`;

const Banner = styled.div<{ $placement: Placement }>`
  box-sizing: border-box;
  width: 100%;
  min-height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 0 16px;
  background: #ffc44d;
  color: #302300;
  font-size: 14px;
  line-height: 20px;

  ${({ $placement }) =>
    $placement === 'page' &&
    `
      position: fixed;
      top: 64px;
      left: 0;
      z-index: 1000;
    `}

  @media (max-width: 1023px) {
    min-height: 44px;
    ${({ $placement }) => $placement === 'page' && 'top: 0;'}
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 24px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -4px;
  }

  @media (max-width: 1023px) {
    width: 44px;
    height: 44px;
  }
`;

function DraftNotice({ placement }: { placement: Placement }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const notice = (
    <Banner $placement={placement} role="status" aria-label="Draft notice">
      <span>
        <strong>Draft</strong> · This item is not published.
      </span>
      <CloseButton
        type="button"
        aria-label="Dismiss draft notice"
        onClick={() => setDismissed(true)}
      >
        <span aria-hidden="true">×</span>
      </CloseButton>
    </Banner>
  );

  return placement === 'page'
    ? createPortal(
        <>
          <PageSpacing />
          {notice}
        </>,
        document.body
      )
    : notice;
}

export default function DraftBanner({ itemKey, draft, placement = 'inline' }: DraftBannerProps) {
  return draft ? <DraftNotice key={itemKey} placement={placement} /> : null;
}
